import { useState, useEffect } from 'react';

interface WaitTimeData {
  airport: string;
  waitTime: number;
  status: 'low' | 'medium' | 'high' | 'Unknown';
  statusColor: string;
  lastUpdated?: string;
  error: boolean;
  estimated?: boolean;
}

/** Airport tiers for estimation fallback */
const AIRPORT_TIERS: Record<string, 'major' | 'large' | 'medium'> = {
  ATL: 'major', LAX: 'major', ORD: 'major', DFW: 'major', DEN: 'major',
  JFK: 'major', SFO: 'major', SEA: 'large', LAS: 'large', MCO: 'large',
  EWR: 'major', CLT: 'large', PHX: 'large', IAH: 'large', MIA: 'major',
  BOS: 'large', MSP: 'large', DTW: 'large', PHL: 'large', LGA: 'major',
};

const BASE_WAIT: Record<string, number> = { major: 18, large: 12, medium: 8 };

const HOUR_MULTIPLIERS = [
  0.5, 0.4, 0.3, 0.3, 0.5, 1.3, 1.6, 1.8,
  1.4, 1.0, 0.8, 0.9, 1.0, 1.0, 1.1, 1.3,
  1.5, 1.6, 1.4, 1.0, 0.8, 0.7, 0.6, 0.5,
];

const DAY_MULTIPLIERS = [1.3, 1.2, 0.9, 0.9, 1.0, 1.3, 1.1];

const getEstimate = (code: string): number => {
  const now = new Date();
  const tier = AIRPORT_TIERS[code] || 'medium';
  const base = BASE_WAIT[tier];
  const mul = HOUR_MULTIPLIERS[now.getHours()] * DAY_MULTIPLIERS[now.getDay()];
  const jitter = Math.round((Math.random() - 0.5) * 4);
  return Math.max(2, Math.round(base * mul) + jitter);
};

const getStatusFromWait = (waitTime: number): { status: 'low' | 'medium' | 'high'; statusColor: string } => {
  if (waitTime <= 10) return { status: 'low', statusColor: 'green' };
  if (waitTime <= 20) return { status: 'medium', statusColor: 'yellow' };
  return { status: 'high', statusColor: 'red' };
};

export const useDemoWaitTimes = () => {
  const [waitTimes, setWaitTimes] = useState<Record<string, WaitTimeData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWaitTimes = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-demo-wait-times`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch wait times');

        const data = await response.json();
        
        // Check if API returned zeroes (expired key) and use estimates instead
        const processed: Record<string, WaitTimeData> = {};
        for (const [code, entry] of Object.entries(data as Record<string, any>)) {
          if (entry.waitTime === null || entry.waitTime === undefined || entry.error) {
            const est = getEstimate(code);
            const statusInfo = getStatusFromWait(est);
            processed[code] = {
              airport: code,
              waitTime: est,
              status: statusInfo.status,
              statusColor: statusInfo.statusColor,
              error: false,
              estimated: true,
            };
          } else {
            processed[code] = { ...entry, estimated: false };
          }
        }
        
        setWaitTimes(processed);
        setError(null);
      } catch (err) {
        console.error('Error fetching demo wait times:', err);
        setError('Unable to load live wait times');
        // Generate estimation fallbacks
        const codes = ['SFO', 'MSP', 'STL', 'LAX', 'ORD'];
        const fallback: Record<string, WaitTimeData> = {};
        for (const code of codes) {
          const est = getEstimate(code);
          const statusInfo = getStatusFromWait(est);
          fallback[code] = {
            airport: code,
            waitTime: est,
            status: statusInfo.status,
            statusColor: statusInfo.statusColor,
            error: false,
            estimated: true,
          };
        }
        setWaitTimes(fallback);
      } finally {
        setLoading(false);
      }
    };

    fetchWaitTimes();
    const interval = setInterval(fetchWaitTimes, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return { waitTimes, loading, error };
};
